import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Cookie, Shield, Settings, X } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'bookingflows_cookie_consent';
const GA_MEASUREMENT_ID = 'G-1GPFME8G3H'; // BookingFlows GA4

// Load Google Analytics only when consent is given
const loadGoogleAnalytics = (measurementId) => {
  if (!measurementId || measurementId === 'G-XXXXXXXXXX') {
    return;
  }
  
  // Check if already loaded
  if (window.gtag) {
    return;
  }
  
  // Load gtag.js script
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  document.head.appendChild(script);
  
  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    anonymize_ip: true, // GDPR compliance
    cookie_flags: 'SameSite=None;Secure'
  });
  
};

// Disable Google Analytics
const disableGoogleAnalytics = () => {
  if (GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX') {
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  }
};

// Check and apply existing consent on app load
export const applyExistingConsent = () => {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      const consent = JSON.parse(stored);
      if (consent.accepted && consent.preferences?.analytics) {
        loadGoogleAnalytics(GA_MEASUREMENT_ID);
      } else {
        disableGoogleAnalytics();
      }
    }
  } catch (e) {
    console.error('Error applying consent:', e);
  }
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: true,
    marketing: false
  });

  useEffect(() => {
    // Don't show cookie consent on customer-facing pages
    const isCustomerPage = window.location.pathname.includes('/customer-ticket') ||
                           window.location.pathname.includes('/public/') ||
                           window.location.pathname.includes('/book/') ||
                           window.location.pathname.includes('/widget');
    
    if (isCustomerPage) {
      // Customer pages don't need cookie consent - they don't use analytics
      return;
    }
    
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after 1 second delay
      const timer = setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => setIsAnimating(true), 50);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Apply existing consent
      applyExistingConsent();
    }
  }, []);

  const saveConsent = (accepted, prefs) => {
    const consentData = {
      accepted,
      timestamp: new Date().toISOString(),
      preferences: prefs,
      version: '1.0'
    };
    
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    
    // Apply consent immediately
    if (accepted && prefs.analytics) {
      loadGoogleAnalytics(GA_MEASUREMENT_ID);
    } else {
      disableGoogleAnalytics();
    }
    
    closeConsent();
  };

  const handleAcceptAll = () => {
    saveConsent(true, {
      necessary: true,
      analytics: true,
      marketing: true
    });
  };

  const handleDecline = () => {
    saveConsent(false, {
      necessary: true,
      analytics: false,
      marketing: false
    });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences.analytics || preferences.marketing, preferences);
  };

  const closeConsent = () => {
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  if (!isVisible) return null;

  // Preferences Modal
  if (showPreferences) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Cookie Preferences</h3>
              <button
                onClick={() => setShowPreferences(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-5">
              Essential cookies are always active for basic functionality.
            </p>
            
            {/* Cookie Categories */}
            <div className="space-y-3">
              {/* Essential - Always on */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">Essential</h4>
                    <p className="text-xs text-gray-500">Required for site functionality</p>
                  </div>
                  <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">Always On</span>
                </div>
              </div>
              
              {/* Analytics */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1 mr-4">
                    <h4 className="font-medium text-gray-900 text-sm">Analytics</h4>
                    <p className="text-xs text-gray-500">Understand site usage</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2663eb]/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2663eb]"></div>
                  </label>
                </div>
              </div>
              
              {/* Marketing */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1 mr-4">
                    <h4 className="font-medium text-gray-900 text-sm">Marketing</h4>
                    <p className="text-xs text-gray-500">Personalized ads</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2663eb]/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2663eb]"></div>
                  </label>
                </div>
              </div>
            </div>
            
            {/* CCPA Notice - Concise */}
            <p className="mt-4 text-xs text-gray-500 text-center">
              We don&apos;t sell your data. <a href="/privacy" className="text-[#2663eb] underline">Privacy Policy</a>
            </p>
            
            {/* Actions */}
            <div className="flex gap-3 mt-5">
              <Button
                variant="outline"
                onClick={handleDecline}
                className="flex-1"
              >
                Reject All
              </Button>
              <Button
                onClick={handleSavePreferences}
                className="flex-1 bg-[#2663eb] hover:bg-[#1d4ed8] text-white"
              >
                Save Preferences
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Slim Desktop Bar / Mobile Card
  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 z-[9999] transition-all duration-300 ease-out ${
        isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
    >
      {/* Desktop: Slim bar */}
      <div className="hidden md:block bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Cookie className="w-5 h-5 text-[#2663eb] flex-shrink-0" />
              <p className="text-sm text-gray-600 truncate">
                We use cookies to enhance your experience.{' '}
                <a href="/privacy" className="text-[#2663eb] hover:underline">Privacy Policy</a>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setShowPreferences(true)}
                className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded hover:bg-gray-100 transition-colors flex items-center gap-1"
              >
                <Settings className="w-4 h-4" />
                Manage
              </button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDecline}
                className="text-gray-700 border-gray-300"
              >
                Decline
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="bg-[#2663eb] hover:bg-[#1d4ed8] text-white"
              >
                Accept All
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: Compact Card */}
      <div className="md:hidden p-3">
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Cookie className="w-4 h-4 text-[#2663eb] flex-shrink-0" />
                <p className="text-xs text-gray-600 truncate">
                  We use cookies.{' '}
                  <a href="/privacy" className="text-[#2663eb] underline">Privacy</a>
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDecline}
                  className="text-xs px-2 h-7 text-gray-600 border-gray-300"
                  data-testid="cookie-decline"
                >
                  Decline
                </Button>
                <Button
                  size="sm"
                  onClick={handleAcceptAll}
                  className="text-xs px-3 h-7 bg-[#2663eb] hover:bg-[#1d4ed8] text-white"
                  data-testid="cookie-accept-all"
                >
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
